/**
 * React Query hooks for BTX rates management
 *
 * Provides individual hooks for rate schedule and rate queries and mutations.
 * Uses React Query for caching, loading states, and automatic refetching.
 */

import { useCallback, useEffect } from "react";
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
  UseQueryOptions,
  UseMutationOptions,
  QueryClient,
} from "@tanstack/react-query";
import { AxiosInstance } from "axios";
import { extractErrorMessage } from "@platform/api";
import { useCellReviews } from "@platform/plan-grid-core";

import {
  RateSchedule,
  RateScheduleInput,
  RateCategory,
  Rate,
  CreateRateScheduleRequest,
  ListRateSchedulesResponse,
  UpdateRateScheduleRequest,
  CreateRateRequest,
  UpdateRateRequest,
  CopyRateSchedulesRequest,
} from "../types";
import { useRatesErrorHandler } from "../context/RatesErrorContext";
import {
  listRateSchedules as apiListRateSchedules,
  getRateSchedule as apiGetRateSchedule,
  createRateSchedule as apiCreateRateSchedule,
  updateRateSchedule as apiUpdateRateSchedule,
  copyRateSchedules as apiCopyRateSchedules,
  deleteRateSchedulesByCategory as apiDeleteRateSchedulesByCategory,
} from "../api/rate-schedules";
import {
  createRate as apiCreateRate,
  updateRate as apiUpdateRate,
  updateProposalRate as apiUpdateProposalRate,
  replaceRateSchedules as apiReplaceRateSchedules,
  replaceProposalRateSchedules as apiReplaceProposalRateSchedules,
} from "../api/rates";
import { PROPOSAL_PLAN_TYPE } from "../constants";
import { rateQueryKeys } from "../utils/query-keys";
import { RATE_CELL_FIELDS, buildRateFieldPath } from "../utils/rateFieldPath";

import { benefitPlanGroupQueryKeys } from "./useBenefitPlanGroups";

import type { BenefitPlan } from "../types/models";

export const RATE_SCHEDULES_STALE_TIME_MS = 5 * 60 * 1000;

// Per QueryClient and plan, the ids of schedules a replace or paste displaced
// from the cache. Quotes doesn't refetch proposals after a rate write, so the
// embedded payload can still carry those schedules; one made up only of them
// is stale and must not refill a slot the server emptied. Any other payload is
// fresh (it reflects the write, or a later change) and ends the suppression.
const displacedScheduleIdsByPlan = new WeakMap<
  QueryClient,
  Map<string, Set<string>>
>();

const recordDisplacedSchedules = (
  queryClient: QueryClient,
  planId: string,
): void => {
  const displacedByPlan =
    displacedScheduleIdsByPlan.get(queryClient) ??
    new Map<string, Set<string>>();
  const previousSchedules =
    queryClient.getQueryData<ListRateSchedulesResponse>(
      rateQueryKeys.schedules(planId),
    ) ?? [];
  displacedByPlan.set(
    planId,
    new Set([
      ...(displacedByPlan.get(planId) ?? []),
      ...previousSchedules.map((schedule) => schedule.id),
    ]),
  );
  displacedScheduleIdsByPlan.set(queryClient, displacedByPlan);
};

const isDisplacedSnapshot = (
  queryClient: QueryClient,
  planId: string,
  embeddedSchedules: RateSchedule[],
): boolean => {
  const displacedIds = displacedScheduleIdsByPlan.get(queryClient)?.get(planId);
  return (
    displacedIds !== undefined &&
    embeddedSchedules.length > 0 &&
    embeddedSchedules.every((schedule) => displacedIds.has(schedule.id))
  );
};

const forgetDisplacedSchedules = (
  queryClient: QueryClient,
  planId: string,
): void => {
  displacedScheduleIdsByPlan.get(queryClient)?.delete(planId);
};

const updateRateInSchedule = (
  schedule: RateSchedule | undefined,
  updatedRate: Rate,
): RateSchedule | undefined => {
  if (!schedule) {
    return schedule;
  }

  return {
    ...schedule,
    rates: schedule.rates?.map((rate) =>
      rate.id === updatedRate.id ? { ...rate, ...updatedRate } : rate,
    ),
  };
};

const updateRateInScheduleList = (
  schedules: ListRateSchedulesResponse | undefined,
  scheduleId: string,
  updatedRate: Rate,
): ListRateSchedulesResponse | undefined => {
  if (!schedules) {
    return schedules;
  }

  return schedules.map((schedule) =>
    schedule.id === scheduleId
      ? updateRateInSchedule(schedule, updatedRate)!
      : schedule,
  );
};

interface RateMutationContext {
  scheduleKey: readonly unknown[];
  scheduleId: string;
  rateId: string;
  /** The rate's value before this edit, for a surgical rollback on failure. */
  previousRate: Rate | undefined;
  benefitPlanId: string | undefined;
  /** This commit's token, from {@link claimLatestRateCommit}. */
  commitToken: symbol;
  /** Review paths of the cells this commit changes, to mark edited once it lands. */
  editedFieldPaths: string[];
}

/**
 * Review paths of every value cell in the given schedules. Each rate design
 * shows its own subset of rate, volume and lives, so every field is included;
 * a path no renderer shows is simply never read.
 */
const allRateCellPaths = (schedules: RateSchedule[]): string[] =>
  schedules.flatMap((schedule) =>
    (schedule.rates ?? []).flatMap((rate) =>
      RATE_CELL_FIELDS.map((field) =>
        buildRateFieldPath(schedule, rate, field),
      ),
    ),
  );

// Tracks the most recent in-flight edit per rate id. Two edits to the same
// cell in quick succession are two independent mutations; their PATCH
// responses can land out of order. Without this, an earlier edit's
// now-stale `onSuccess` would overwrite the cache with its own (older)
// value after a later edit already applied a newer one — visibly reverting
// the cell to what was typed first. Only the mutation holding the current
// token for a rate id is allowed to write that rate's result into the cache;
// a superseded mutation's onSuccess/onError becomes a no-op for the cache.
const latestRateCommitByRateId = new Map<string, symbol>();

const claimLatestRateCommit = (rateId: string): symbol => {
  const token = Symbol(rateId);
  latestRateCommitByRateId.set(rateId, token);
  return token;
};

const isLatestRateCommit = (rateId: string, token: symbol): boolean =>
  latestRateCommitByRateId.get(rateId) === token;

/** Stops tracking a rate id once its latest commit has settled. */
const releaseRateCommit = (rateId: string, token: symbol): void => {
  if (isLatestRateCommit(rateId, token)) {
    latestRateCommitByRateId.delete(rateId);
  }
};

/**
 * Writes the edited rate into the cache immediately on commit, rather than
 * waiting for the PATCH round-trip, so the cell's formatted value (decimal
 * padding, etc. — derived from the cache, not local input state) updates
 * without the visible lag a network round-trip adds. `onSuccess` below still
 * overwrites this with the server-confirmed value once it lands.
 */
const applyOptimisticRateUpdate = async (
  queryClient: QueryClient,
  variables: UpdateRateRequest,
): Promise<RateMutationContext> => {
  const commitToken = claimLatestRateCommit(variables.rateId);
  const scheduleKey = rateQueryKeys.schedule(variables.scheduleId);

  await queryClient.cancelQueries({ queryKey: scheduleKey });

  const previousSchedule = queryClient.getQueryData<RateSchedule>(scheduleKey);
  const benefitPlanId =
    variables.benefitPlanId ?? previousSchedule?.benefitPlanId;

  if (benefitPlanId) {
    await queryClient.cancelQueries({
      queryKey: rateQueryKeys.schedules(benefitPlanId),
    });
  }

  const previousSchedules = benefitPlanId
    ? queryClient.getQueryData<ListRateSchedulesResponse>(
        rateQueryKeys.schedules(benefitPlanId),
      )
    : undefined;

  // Renderers read live rate data from the schedules LIST cache (seeded by
  // useResolvedRateSchedules), not from this scheduleId-keyed slot — nothing
  // populates that slot in the RatesGrid flow, so look in the list first.
  const scheduleFromList = previousSchedules?.find(
    (schedule) => schedule.id === variables.scheduleId,
  );
  const existingRate =
    scheduleFromList?.rates?.find((rate) => rate.id === variables.rateId) ??
    previousSchedule?.rates?.find((rate) => rate.id === variables.rateId);

  if (existingRate) {
    // `variables.rate` allows `null` to clear a field; `Rate`'s own fields
    // don't, so normalize before merging into the optimistic cache value.
    const patch = Object.fromEntries(
      Object.entries(variables.rate).map(([key, value]) => [
        key,
        value ?? undefined,
      ]),
    );
    const optimisticRate: Rate = { ...existingRate, ...patch };

    queryClient.setQueryData<RateSchedule | undefined>(scheduleKey, (current) =>
      updateRateInSchedule(current, optimisticRate),
    );

    if (benefitPlanId) {
      queryClient.setQueryData<ListRateSchedulesResponse | undefined>(
        rateQueryKeys.schedules(benefitPlanId),
        (current) =>
          updateRateInScheduleList(
            current,
            variables.scheduleId,
            optimisticRate,
          ),
      );
    }
  }

  // The path is built from the rate's coordinates (tier, band, unit), which a
  // value edit never changes, so the pre-edit rate keys it correctly.
  const editedFieldPaths =
    scheduleFromList && existingRate
      ? RATE_CELL_FIELDS.filter((field) => field in variables.rate).map(
          (field) => buildRateFieldPath(scheduleFromList, existingRate, field),
        )
      : [];

  return {
    scheduleKey,
    scheduleId: variables.scheduleId,
    rateId: variables.rateId,
    previousRate: existingRate,
    benefitPlanId,
    commitToken,
    editedFieldPaths,
  };
};

/**
 * Reverts just the edited rate back to its pre-edit value on a failed
 * commit, applied against whatever the cache holds NOW rather than a
 * snapshot taken at mutate time — other rates/schedules may have legitimately
 * changed in the cache while this mutation was in flight, and restoring a
 * stale whole-list snapshot would wipe those out along with this rollback.
 */
const rollbackOptimisticRateUpdate = (
  queryClient: QueryClient,
  context: RateMutationContext | undefined,
): void => {
  if (!context || !context.previousRate) return;

  const { previousRate } = context;

  queryClient.setQueryData<RateSchedule | undefined>(
    context.scheduleKey,
    (current) => updateRateInSchedule(current, previousRate),
  );

  if (context.benefitPlanId) {
    queryClient.setQueryData<ListRateSchedulesResponse | undefined>(
      rateQueryKeys.schedules(context.benefitPlanId),
      (current) =>
        updateRateInScheduleList(current, context.scheduleId, previousRate),
    );
  }
};

/**
 * Hook to list rate schedules for a benefit plan
 *
 * @param benefitPlanId - Benefit plan ID
 * @param axiosInstance - Axios instance for making API calls
 * @param options - React Query options
 * @returns Query result with list of rate schedules
 *
 * @example
 * ```tsx
 * function RateSchedulesList({ benefitPlanId }: { benefitPlanId: string }) {
 *   const axiosInstance = useApi();
 *   const { data, isLoading, error } = useListRateSchedules(benefitPlanId, axiosInstance);
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return (
 *     <ul>
 *       {data?.rateSchedules.map(schedule => (
 *         <li key={schedule.id}>{schedule.rateDesign}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export const useListRateSchedules = (
  benefitPlanId: string,
  axiosInstance: AxiosInstance,
  options?: Omit<
    UseQueryOptions<ListRateSchedulesResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListRateSchedulesResponse, Error> =>
  useQuery({
    queryKey: rateQueryKeys.schedules(benefitPlanId),
    queryFn: () => apiListRateSchedules(benefitPlanId, axiosInstance),
    enabled: !!benefitPlanId,
    // Mutations already invalidate this key on success (see updateRate,
    // updateRateSchedule, etc. below), so a remount doesn't need to refetch
    // on top of that to stay accurate.
    staleTime: RATE_SCHEDULES_STALE_TIME_MS,
    ...options,
  });

/**
 * Resolves rate schedules from either proposal data (synchronous) or the API
 * (via React Query), depending on the plan's `rateScheduleSource`.
 *
 * For proposal plans, rate schedules are embedded in the proposal payload and
 * seeded into the React Query cache on mount. Reads go through the cache so
 * that `useUpdateProposalRate` mutations are reflected immediately without
 * requiring a proposals refetch.
 */
export const useResolvedRateSchedules = (
  plan: BenefitPlan,
  axiosInstance?: AxiosInstance,
): { data?: RateSchedule[]; isLoading: boolean } => {
  const isProposal = plan.rateScheduleSource === "proposal";
  const queryClient = useQueryClient();

  // Seed the cache from the embedded proposal payload. This ensures
  // useUpdateProposalRate's setQueryData writes land in the same cache slot
  // that the query below reads from, so inline edits are reflected
  // immediately. A slot holding schedules is never overwritten — in-flight
  // edits survive re-renders — but an EMPTY seeded slot has no edits to
  // preserve, so a non-empty embedded snapshot replaces it: a plan first
  // rendered before its carrier's rates arrived would otherwise keep reading
  // the stale empty slot and stay blank through every proposals refetch until
  // a full reload. While the embedded snapshot still holds only schedules a
  // replace or paste displaced, the plan resolves to no schedules, even once
  // React Query has garbage-collected the slot that write emptied.
  const planRateSchedule = plan.rateSchedule;
  const isEmbeddedSnapshotDisplaced =
    isProposal &&
    planRateSchedule !== undefined &&
    isDisplacedSnapshot(queryClient, plan.id, planRateSchedule);
  const embeddedSchedules = isEmbeddedSnapshotDisplaced ? [] : planRateSchedule;
  useEffect(() => {
    if (!isProposal || !plan.id) return;
    const schedulesKey = rateQueryKeys.schedules(plan.id);
    const cached =
      queryClient.getQueryData<ListRateSchedulesResponse>(schedulesKey);
    if (cached?.length || !planRateSchedule) {
      queryClient.setQueryData(schedulesKey, cached ?? []);
      return;
    }
    if (isDisplacedSnapshot(queryClient, plan.id, planRateSchedule)) {
      queryClient.setQueryData(schedulesKey, []);
      return;
    }
    forgetDisplacedSchedules(queryClient, plan.id);
    queryClient.setQueryData(schedulesKey, planRateSchedule);
  }, [isProposal, plan.id, planRateSchedule, queryClient]);

  // For proposal plans: reads from cache (seeded above, updated by mutations).
  // For plans that already arrived with an embedded schedule but aren't
  // proposal-sourced (e.g. the synthetic Requested/Incumbent column, which
  // may have no backend benefit-plan record at all): skip the fetch entirely
  // — it would 404 downstream, or in tests with strict fixture mocking,
  // throw on an unregistered request.
  // For genuine BTX plans with nothing embedded yet: fetch from the API.
  const hasEmbeddedSchedule = planRateSchedule !== undefined;
  const result = useListRateSchedules(plan.id, axiosInstance as AxiosInstance, {
    enabled:
      !isProposal && !hasEmbeddedSchedule && !!axiosInstance && !!plan.id,
  });

  return {
    data: result.data ?? embeddedSchedules,
    isLoading: result.isLoading,
  };
};

/**
 * Hook to get a single rate schedule by ID
 *
 * @param scheduleId - Rate schedule ID
 * @param axiosInstance - Axios instance for making API calls
 * @param options - React Query options
 * @returns Query result with rate schedule data
 *
 * @example
 * ```tsx
 * function RateScheduleDetails({ scheduleId }: { scheduleId: string }) {
 *   const axiosInstance = useApi();
 *   const { data: schedule, isLoading } = useGetRateSchedule(scheduleId, axiosInstance);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <h2>{schedule?.rateDesign}</h2>
 *       <p>Rates: {schedule?.rates?.length}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export const useGetRateSchedule = (
  scheduleId: string,
  axiosInstance: AxiosInstance,
  options?: Omit<UseQueryOptions<RateSchedule, Error>, "queryKey" | "queryFn">,
): UseQueryResult<RateSchedule, Error> =>
  useQuery({
    queryKey: rateQueryKeys.schedule(scheduleId),
    queryFn: () => apiGetRateSchedule(scheduleId, axiosInstance),
    enabled: !!scheduleId,
    ...options,
  });

/**
 * Hook to create a new rate schedule
 *
 * Automatically invalidates the schedules list for the benefit plan on success.
 *
 * @param axiosInstance - Axios instance for making API calls
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function CreateScheduleButton({ benefitPlanId }: { benefitPlanId: string }) {
 *   const axiosInstance = useApi();
 *   const mutation = useCreateRateSchedule(axiosInstance, {
 *     onSuccess: () => {
 *       toast.success('Rate schedule created!');
 *     },
 *   });
 *
 *   const handleCreate = () => {
 *     mutation.mutate({
 *       benefitPlanId,
 *       rateSchedules: [{
 *         rateDesign: 'ageBanded',
 *         rates: [{ ageBandLower: 18, rate: 0.50, unit: 'volumePer1000' }]
 *       }]
 *     });
 *   };
 *
 *   return (
 *     <button onClick={handleCreate} disabled={mutation.isPending}>
 *       {mutation.isPending ? 'Creating...' : 'Create Schedule'}
 *     </button>
 *   );
 * }
 * ```
 */
export const useCreateRateSchedule = (
  axiosInstance: AxiosInstance,
  options?: UseMutationOptions<
    RateSchedule[],
    Error,
    CreateRateScheduleRequest,
    unknown
  >,
): UseMutationResult<
  RateSchedule[],
  Error,
  CreateRateScheduleRequest,
  unknown
> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();

  return useMutation({
    mutationFn: (request: CreateRateScheduleRequest) =>
      apiCreateRateSchedule(request, axiosInstance),
    ...options,
    onSuccess: (
      data: RateSchedule[],
      variables: CreateRateScheduleRequest,
      context: unknown,
    ) => {
      // Invalidate list query for the benefit plan
      queryClient.invalidateQueries({
        queryKey: rateQueryKeys.schedules(variables.benefitPlanId),
      });

      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (
      error: Error,
      variables: CreateRateScheduleRequest,
      context: unknown,
    ) => {
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
  });
};

/**
 * Hook to update rate schedule metadata
 *
 * Automatically invalidates both the individual schedule and the schedules list on success.
 *
 * @param axiosInstance - Axios instance for making API calls
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function UpdateScheduleForm({ scheduleId }: { scheduleId: string }) {
 *   const axiosInstance = useApi();
 *   const mutation = useUpdateRateSchedule(axiosInstance);
 *
 *   const handleUpdate = (values: { numberOfLives: number }) => {
 *     mutation.mutate({
 *       id: scheduleId,
 *       rateSchedule: { numberOfLives: values.numberOfLives }
 *     });
 *   };
 *
 *   return <Form onSubmit={handleUpdate} />;
 * }
 * ```
 */
export const useUpdateRateSchedule = (
  axiosInstance: AxiosInstance,
  options?: UseMutationOptions<
    RateSchedule,
    Error,
    UpdateRateScheduleRequest,
    unknown
  >,
): UseMutationResult<
  RateSchedule,
  Error,
  UpdateRateScheduleRequest,
  unknown
> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();

  return useMutation({
    mutationFn: (request: UpdateRateScheduleRequest) =>
      apiUpdateRateSchedule(request, axiosInstance),
    ...options,
    onSuccess: (
      data: RateSchedule,
      variables: UpdateRateScheduleRequest,
      context: unknown,
    ) => {
      // Invalidate both the individual schedule and the list
      queryClient.invalidateQueries({
        queryKey: rateQueryKeys.schedule(variables.id),
      });

      // Also invalidate the list query
      if (data.benefitPlanId) {
        queryClient.invalidateQueries({
          queryKey: rateQueryKeys.schedules(data.benefitPlanId),
        });
      }

      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (
      error: Error,
      variables: UpdateRateScheduleRequest,
      context: unknown,
    ) => {
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
  });
};

/**
 * Hook to create an individual rate within a rate schedule
 *
 * Automatically invalidates the rate schedule and schedules list on success.
 * Creating a rate may affect schedule metadata (e.g., calculated totals) shown in the list view.
 *
 * @param axiosInstance - Axios instance for making API calls
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function AddRateButton({ scheduleId }: { scheduleId: string }) {
 *   const axiosInstance = useApi();
 *   const mutation = useCreateRate(axiosInstance);
 *
 *   const handleAdd = () => {
 *     mutation.mutate({
 *       scheduleId,
 *       rate: {
 *         ageBandLower: 30,
 *         ageBandUpper: 39,
 *         rate: 1.25,
 *         unit: 'volumePer1000'
 *       }
 *     });
 *   };
 *
 *   return <button onClick={handleAdd}>Add Rate</button>;
 * }
 * ```
 */
export const useCreateRate = (
  axiosInstance: AxiosInstance,
  options?: UseMutationOptions<Rate, Error, CreateRateRequest, unknown>,
): UseMutationResult<Rate, Error, CreateRateRequest, unknown> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();

  return useMutation({
    mutationFn: (request: CreateRateRequest) =>
      apiCreateRate(request, axiosInstance),
    ...options,
    onSuccess: async (
      data: Rate,
      variables: CreateRateRequest,
      context: unknown,
    ) => {
      // Invalidate the schedule to refetch with new rate
      queryClient.invalidateQueries({
        queryKey: rateQueryKeys.schedule(variables.scheduleId),
      });

      // Also invalidate schedules list because rate creation may affect schedule metadata
      // (e.g., calculated totals) that appears in the list view
      // We need to get the benefitPlanId from the schedule query cache
      const scheduleData = queryClient.getQueryData<RateSchedule>(
        rateQueryKeys.schedule(variables.scheduleId),
      );

      if (scheduleData?.benefitPlanId) {
        queryClient.invalidateQueries({
          queryKey: rateQueryKeys.schedules(scheduleData.benefitPlanId),
        });
      }

      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (error: Error, variables: CreateRateRequest, context: unknown) => {
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
  });
};

/**
 * Hook to update an individual rate
 *
 * Automatically invalidates the rate schedule and schedules list on success.
 *
 * @param axiosInstance - Axios instance for making API calls
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function RateEditor({ scheduleId, rateId }: { scheduleId: string; rateId: string }) {
 *   const axiosInstance = useApi();
 *   const mutation = useUpdateRate(axiosInstance);
 *
 *   const handleUpdate = (newRate: number) => {
 *     mutation.mutate({
 *       scheduleId,
 *       rateId,
 *       rate: { rate: newRate }
 *     });
 *   };
 *
 *   return <input type="number" onChange={(e) => handleUpdate(Number(e.target.value))} />;
 * }
 * ```
 */
export const useUpdateRate = (
  axiosInstance: AxiosInstance,
  options?: UseMutationOptions<
    Rate,
    Error,
    UpdateRateRequest,
    RateMutationContext | undefined
  >,
): UseMutationResult<
  Rate,
  Error,
  UpdateRateRequest,
  RateMutationContext | undefined
> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();
  const { setEdited } = useCellReviews();

  return useMutation({
    mutationFn: (request: UpdateRateRequest) =>
      apiUpdateRate(request, axiosInstance),
    ...options,
    onMutate: (variables) => applyOptimisticRateUpdate(queryClient, variables),
    onSuccess: async (
      data: Rate,
      variables: UpdateRateRequest,
      context: RateMutationContext | undefined,
    ) => {
      const editedPlanId = context?.benefitPlanId;
      if (editedPlanId) {
        context.editedFieldPaths.forEach((fieldPath) =>
          setEdited(editedPlanId, fieldPath),
        );
      }

      // A newer edit to this same rate has since committed; that mutation's
      // own onSuccess/onError owns the cache now — writing this stale
      // response would revert the cell to an older value.
      if (
        context &&
        !isLatestRateCommit(variables.rateId, context.commitToken)
      ) {
        options?.onSuccess?.(data, variables, context, undefined as never);
        return;
      }

      const scheduleKey = rateQueryKeys.schedule(variables.scheduleId);
      const existingSchedule =
        queryClient.getQueryData<RateSchedule>(scheduleKey);

      queryClient.setQueryData<RateSchedule | undefined>(
        scheduleKey,
        (currentSchedule) => updateRateInSchedule(currentSchedule, data),
      );

      // Also update the schedules list cache since rate updates may affect schedule metadata.
      // No invalidation needed — setQueryData already applies the server response,
      // so a refetch would return the same data but create new references that
      // cause unnecessary re-renders (and lose input focus in rate tables).
      let benefitPlanId = variables.benefitPlanId;

      if (!benefitPlanId) {
        benefitPlanId = existingSchedule?.benefitPlanId;
      }

      if (benefitPlanId) {
        queryClient.setQueryData<ListRateSchedulesResponse | undefined>(
          rateQueryKeys.schedules(benefitPlanId),
          (currentSchedules) =>
            updateRateInScheduleList(
              currentSchedules,
              variables.scheduleId,
              data,
            ),
        );
      }

      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (
      error: Error,
      variables: UpdateRateRequest,
      context: RateMutationContext | undefined,
    ) => {
      if (
        context &&
        isLatestRateCommit(variables.rateId, context.commitToken)
      ) {
        rollbackOptimisticRateUpdate(queryClient, context);
      }
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
    onSettled: (_data, _error, variables, context) => {
      if (context) releaseRateCommit(variables.rateId, context.commitToken);
    },
  });
};

export const useUpdateProposalRate = (
  axiosInstance: AxiosInstance,
  options?: UseMutationOptions<
    Rate,
    Error,
    UpdateRateRequest,
    RateMutationContext | undefined
  >,
): UseMutationResult<
  Rate,
  Error,
  UpdateRateRequest,
  RateMutationContext | undefined
> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();

  return useMutation({
    mutationFn: (request: UpdateRateRequest) =>
      apiUpdateProposalRate(request, axiosInstance),
    ...options,
    onMutate: (variables) => applyOptimisticRateUpdate(queryClient, variables),
    onSuccess: async (
      data: Rate,
      variables: UpdateRateRequest,
      context: RateMutationContext | undefined,
    ) => {
      // See useUpdateRate above: a newer edit to this rate has since
      // committed, so skip writing this now-stale response into the cache.
      if (
        context &&
        !isLatestRateCommit(variables.rateId, context.commitToken)
      ) {
        options?.onSuccess?.(data, variables, context, undefined as never);
        return;
      }

      const scheduleKey = rateQueryKeys.schedule(variables.scheduleId);
      const existingSchedule =
        queryClient.getQueryData<RateSchedule>(scheduleKey);

      queryClient.setQueryData<RateSchedule | undefined>(
        scheduleKey,
        (currentSchedule) => updateRateInSchedule(currentSchedule, data),
      );

      let benefitPlanId = variables.benefitPlanId;
      if (!benefitPlanId) {
        benefitPlanId = existingSchedule?.benefitPlanId;
      }

      if (benefitPlanId) {
        queryClient.setQueryData<ListRateSchedulesResponse | undefined>(
          rateQueryKeys.schedules(benefitPlanId),
          (currentSchedules) =>
            updateRateInScheduleList(
              currentSchedules,
              variables.scheduleId,
              data,
            ),
        );
      }

      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (
      error: Error,
      variables: UpdateRateRequest,
      context: RateMutationContext | undefined,
    ) => {
      if (
        context &&
        isLatestRateCommit(variables.rateId, context.commitToken)
      ) {
        rollbackOptimisticRateUpdate(queryClient, context);
      }
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
    onSettled: (_data, _error, variables, context) => {
      if (context) releaseRateCommit(variables.rateId, context.commitToken);
    },
  });
};

export const useReplaceRateSchedules = (
  axiosInstance: AxiosInstance,
  options?: UseMutationOptions<
    RateSchedule[],
    Error,
    { benefitPlanId: string; rateSchedules: RateScheduleInput[] },
    unknown
  >,
): UseMutationResult<
  RateSchedule[],
  Error,
  { benefitPlanId: string; rateSchedules: RateScheduleInput[] },
  unknown
> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();

  return useMutation({
    mutationFn: ({ benefitPlanId, rateSchedules }) =>
      apiReplaceRateSchedules(benefitPlanId, rateSchedules, axiosInstance),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData<ListRateSchedulesResponse | undefined>(
        rateQueryKeys.schedules(variables.benefitPlanId),
        () => data,
      );
      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (error, variables, context) => {
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
  });
};

type ReplaceProposalRateSchedulesVariables = {
  proposalPlanId: string;
  rateSchedules: RateScheduleInput[];
};

export const useReplaceProposalRateSchedules = (
  axiosInstance: AxiosInstance,
  options?: UseMutationOptions<
    RateSchedule[],
    Error,
    ReplaceProposalRateSchedulesVariables,
    unknown
  >,
): UseMutationResult<
  RateSchedule[],
  Error,
  ReplaceProposalRateSchedulesVariables,
  unknown
> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();

  return useMutation({
    mutationFn: ({ proposalPlanId, rateSchedules }) =>
      apiReplaceProposalRateSchedules(
        proposalPlanId,
        rateSchedules,
        axiosInstance,
      ),
    ...options,
    onSuccess: (data, variables, context) => {
      recordDisplacedSchedules(queryClient, variables.proposalPlanId);
      queryClient.setQueryData<ListRateSchedulesResponse | undefined>(
        rateQueryKeys.schedules(variables.proposalPlanId),
        () => data,
      );
      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (error, variables, context) => {
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
  });
};

type ReplaceRateSchedulesByCategoryVariables = {
  benefitPlanId: string;
  category: RateCategory;
  rateSchedules: RateScheduleInput[];
};

/**
 * Hook to replace a benefit plan's renewal or revisedRenewal rate schedules. On success
 * it invalidates the renewal-inclusive list so it refetches; the category-scoped
 * response is not written into the cache directly because it omits the plan's other
 * categories (current and, for a renewal write, revisedRenewal).
 */
export const useReplaceRateSchedulesByCategory = (
  axiosInstance: AxiosInstance,
  options?: UseMutationOptions<
    RateSchedule[],
    Error,
    ReplaceRateSchedulesByCategoryVariables,
    unknown
  >,
): UseMutationResult<
  RateSchedule[],
  Error,
  ReplaceRateSchedulesByCategoryVariables,
  unknown
> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();

  return useMutation({
    mutationFn: ({ benefitPlanId, category, rateSchedules }) =>
      apiReplaceRateSchedules(
        benefitPlanId,
        rateSchedules,
        axiosInstance,
        category,
      ),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: rateQueryKeys.schedulesWithRenewal(variables.benefitPlanId),
      });
      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (error, variables, context) => {
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
  });
};

type DeleteRateSchedulesByCategoryVariables = {
  benefitPlanId: string;
  category: RateCategory;
};

/**
 * Hook to clear a benefit plan's renewal or revisedRenewal rate schedules. Invalidates
 * the renewal-inclusive list so it refetches without the cleared category.
 */
export const useDeleteRateSchedulesByCategory = (
  axiosInstance: AxiosInstance,
  options?: UseMutationOptions<
    void,
    Error,
    DeleteRateSchedulesByCategoryVariables,
    unknown
  >,
): UseMutationResult<
  void,
  Error,
  DeleteRateSchedulesByCategoryVariables,
  unknown
> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();

  return useMutation({
    mutationFn: ({ benefitPlanId, category }) =>
      apiDeleteRateSchedulesByCategory(benefitPlanId, category, axiosInstance),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: rateQueryKeys.schedulesWithRenewal(variables.benefitPlanId),
      });
      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (error, variables, context) => {
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
  });
};

/**
 * Hook to fetch a benefit plan's rate schedules including its renewal and revisedRenewal
 * categories — the PPX/MP opt-in read. Cached under a key distinct from the default
 * current-only list so the two never overwrite each other.
 */
export const useListRateSchedulesWithRenewal = (
  benefitPlanId: string,
  axiosInstance: AxiosInstance,
  options?: Omit<
    UseQueryOptions<ListRateSchedulesResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListRateSchedulesResponse, Error> =>
  useQuery({
    queryKey: rateQueryKeys.schedulesWithRenewal(benefitPlanId),
    queryFn: () => apiListRateSchedules(benefitPlanId, axiosInstance, true),
    enabled: !!benefitPlanId,
    ...options,
  });

/**
 * Fetches renewal-inclusive schedules for several benefit plans at once and returns
 * them keyed by plan id, including only plans that resolved to at least one schedule.
 * Used to feed the Quotes SBS incumbent/current column its current + renewal +
 * revisedRenewal rates in one place.
 */
export const useRenewalSchedulesByPlan = (
  benefitPlanIds: string[],
  axiosInstance: AxiosInstance | undefined,
): Record<string, RateSchedule[]> => {
  // combine keeps the returned map referentially stable across renders (React
  // Query only re-runs it when a query's data changes), so it can safely feed a
  // downstream useMemo without busting it every render.
  const combine = useCallback(
    (results: UseQueryResult<ListRateSchedulesResponse, Error>[]) =>
      Object.fromEntries(
        benefitPlanIds
          .map(
            (benefitPlanId, index) =>
              [benefitPlanId, results[index]?.data] as const,
          )
          .filter((entry): entry is readonly [string, RateSchedule[]] =>
            Boolean(entry[1]?.length),
          ),
      ),
    [benefitPlanIds],
  );

  return useQueries({
    queries: benefitPlanIds.map((benefitPlanId) => ({
      queryKey: rateQueryKeys.schedulesWithRenewal(benefitPlanId),
      queryFn: () =>
        apiListRateSchedules(
          benefitPlanId,
          axiosInstance as AxiosInstance,
          true,
        ),
      enabled: !!axiosInstance && !!benefitPlanId,
    })),
    combine,
  });
};

/**
 * Hook to copy all rate schedules from one benefit plan to another.
 * Invalidates the destination plan's rate schedule cache on success.
 */
export const useCopyRateSchedules = (
  axiosInstance: AxiosInstance | undefined,
  options?: UseMutationOptions<
    RateSchedule[],
    Error,
    CopyRateSchedulesRequest,
    unknown
  >,
): UseMutationResult<
  RateSchedule[],
  Error,
  CopyRateSchedulesRequest,
  unknown
> => {
  const queryClient = useQueryClient();
  const showError = useRatesErrorHandler();
  const { setEdited } = useCellReviews();

  return useMutation({
    mutationFn: (request: CopyRateSchedulesRequest) => {
      if (!axiosInstance) {
        return Promise.reject(new Error("axiosInstance is not available"));
      }
      return apiCopyRateSchedules(request, axiosInstance);
    },
    onSuccess: (data, variables, context) => {
      // Proposal plans live on the Quotes tab, which keeps its own edited marks.
      if (variables.destinationPlanType !== PROPOSAL_PLAN_TYPE) {
        allRateCellPaths(data).forEach((fieldPath) =>
          setEdited(variables.destinationBenefitPlanId, fieldPath),
        );
      }

      recordDisplacedSchedules(queryClient, variables.destinationBenefitPlanId);
      queryClient.setQueryData<ListRateSchedulesResponse>(
        rateQueryKeys.schedules(variables.destinationBenefitPlanId),
        data,
      );
      // Refetch benefit plan groups so the grid picks up the new rate schedules
      queryClient.invalidateQueries({
        queryKey: benefitPlanGroupQueryKeys.all,
      });
      options?.onSuccess?.(data, variables, context, undefined as never);
    },
    onError: (error: Error, variables, context) => {
      showError?.(extractErrorMessage(error));
      options?.onError?.(error, variables, context, undefined as never);
    },
  });
};
